import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
  Play,
  CheckCircle,
  XCircle,
  Camera,
  Upload,
  Clock,
  AlertTriangle,
  ClipboardCheck,
  ChevronRight,
  Loader2,
  Image as ImageIcon,
  X,
  Hash,
  Type,
  FileCheck,
  Send,
  Flag,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const responseTypeIcons = {
  pass_fail: CheckCircle,
  numeric: Hash,
  text: Type,
  photo: Camera,
};

const responseTypeLabels = {
  pass_fail: 'Pass/Fail',
  numeric: 'Numeric',
  text: 'Text Input',
  photo: 'Photo',
};

export default function VisitExecutionPanel({ visit, onUpdate, onComplete }) {
  const [executing, setExecuting] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [expandedTask, setExpandedTask] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [currentCheck, setCurrentCheck] = useState(null);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [completionData, setCompletionData] = useState({
    report_summary: '',
    notes: '',
    findings: [],
    recommendations: [],
  });
  const [newFinding, setNewFinding] = useState('');
  const [newRecommendation, setNewRecommendation] = useState('');
  const fileInputRef = useRef(null);

  // Calculate progress
  const allChecks = visit.tasks?.flatMap(t => t.checks || []) || [];
  const completedChecks = allChecks.filter(c => c.response !== null && c.response !== undefined);
  const mandatoryChecks = allChecks.filter(c => c.is_mandatory);
  const completedMandatory = mandatoryChecks.filter(c => c.response !== null && c.response !== undefined);
  const progress = allChecks.length > 0 ? Math.round((completedChecks.length / allChecks.length) * 100) : 0;
  const canComplete = completedMandatory.length === mandatoryChecks.length;

  const startExecution = async () => {
    try {
      setExecuting(true);
      const response = await axios.post(`${API}/site-visits/${visit.id}/start-execution`, {
        gps_location: null, // Could add geolocation here
      });
      setExecutionId(response.data.execution_id);
      onUpdate && onUpdate();
    } catch (error) {
      console.error('Failed to start execution:', error);
      setExecuting(false);
    }
  };

  const submitCheck = async (taskId, check, responseValue, numericValue = null, photoUrl = null, notes = '') => {
    const key = `${taskId}-${check.id}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));
    
    try {
      await axios.post(`${API}/site-visits/${visit.id}/submit-check`, {
        task_id: taskId,
        check_id: check.id,
        response_value: responseValue,
        numeric_value: numericValue,
        photo_url: photoUrl,
        notes: notes,
      });
      onUpdate && onUpdate();
    } catch (error) {
      console.error('Failed to submit check:', error);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handlePhotoCapture = (taskId, check) => {
    setCurrentTaskId(taskId);
    setCurrentCheck(check);
    setPhotoPreview(null);
    setShowPhotoDialog(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const submitPhotoCheck = async () => {
    if (!currentCheck || !currentTaskId) return;
    
    // In a real app, you'd upload the photo to cloud storage
    // For now, we'll use the base64 preview as a placeholder
    await submitCheck(currentTaskId, currentCheck, 'photo_submitted', null, photoPreview, '');
    setShowPhotoDialog(false);
    setPhotoPreview(null);
    setCurrentCheck(null);
    setCurrentTaskId(null);
  };

  const completeExecution = async () => {
    try {
      await axios.post(`${API}/site-visits/${visit.id}/complete-execution`, {
        report_summary: completionData.report_summary,
        notes: completionData.notes,
        findings: completionData.findings,
        recommendations: completionData.recommendations,
      });
      setShowCompleteDialog(false);
      onComplete && onComplete();
      onUpdate && onUpdate();
    } catch (error) {
      console.error('Failed to complete execution:', error);
    }
  };

  const addFinding = () => {
    if (newFinding.trim()) {
      setCompletionData(prev => ({
        ...prev,
        findings: [...prev.findings, { description: newFinding, severity: 'medium' }]
      }));
      setNewFinding('');
    }
  };

  const addRecommendation = () => {
    if (newRecommendation.trim()) {
      setCompletionData(prev => ({
        ...prev,
        recommendations: [...prev.recommendations, newRecommendation]
      }));
      setNewRecommendation('');
    }
  };

  const getTaskProgress = (task) => {
    const checks = task.checks || [];
    const completed = checks.filter(c => c.response !== null && c.response !== undefined);
    return checks.length > 0 ? Math.round((completed.length / checks.length) * 100) : 0;
  };

  const isTaskComplete = (task) => {
    const mandatoryChecks = (task.checks || []).filter(c => c.is_mandatory);
    return mandatoryChecks.every(c => c.response !== null && c.response !== undefined);
  };

  // Not in execution mode - show start button
  if (visit.status === 'scheduled' && !executing) {
    return (
      <Card className="border-sky-200 bg-sky-50/50">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
            <Play className="w-8 h-8 text-sky-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Ready to Execute</h3>
          <p className="text-slate-600 mb-4">
            This visit has {visit.tasks?.length || 0} tasks with {allChecks.length} checks to complete.
          </p>
          <Button onClick={startExecution} size="lg" data-testid="start-execution-btn">
            <Play className="w-4 h-4 mr-2" />
            Start Execution
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Completed visit
  if (visit.status === 'completed') {
    return (
      <Card className="border-emerald-200 bg-emerald-50/50">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-900">Visit Completed</h3>
              <p className="text-sm text-emerald-700">
                {completedChecks.length}/{allChecks.length} checks completed
              </p>
            </div>
          </div>
          
          {visit.report_summary && (
            <div className="bg-white rounded-lg p-4 border border-emerald-200">
              <h4 className="font-medium text-slate-900 mb-2">Summary</h4>
              <p className="text-slate-600 text-sm">{visit.report_summary}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // In Progress - show execution UI
  return (
    <div className="space-y-4">
      {/* Progress Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                <Clock className="w-3 h-3 mr-1" />
                In Progress
              </Badge>
              <span className="text-sm text-slate-600">
                {completedChecks.length}/{allChecks.length} checks
              </span>
            </div>
            <span className="font-semibold text-slate-900">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          
          {!canComplete && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Complete all mandatory checks ({completedMandatory.length}/{mandatoryChecks.length}) to finish
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tasks Accordion */}
      <Accordion type="single" collapsible value={expandedTask} onValueChange={setExpandedTask}>
        {visit.tasks?.map((task, taskIndex) => {
          const taskProgress = getTaskProgress(task);
          const taskComplete = isTaskComplete(task);
          
          return (
            <AccordionItem key={task.id} value={task.id} className="border rounded-lg mb-2 overflow-hidden">
              <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50" data-testid={`task-${task.id}`}>
                <div className="flex items-center gap-3 flex-1">
                  <div className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                    taskComplete 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-slate-100 text-slate-600'
                  )}>
                    {taskComplete ? <CheckCircle className="w-4 h-4" /> : taskIndex + 1}
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-medium text-slate-900">{task.name}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span>{(task.checks || []).length} checks</span>
                      <span>•</span>
                      <span>{task.estimated_minutes || 30} min</span>
                      <span>•</span>
                      <span className={taskComplete ? 'text-emerald-600' : ''}>{taskProgress}%</span>
                    </div>
                  </div>
                  <Progress value={taskProgress} className="w-20 h-1.5" />
                </div>
              </AccordionTrigger>
              
              <AccordionContent className="px-4 pb-4">
                {task.description && (
                  <p className="text-sm text-slate-600 mb-4 pl-11">{task.description}</p>
                )}
                
                <div className="space-y-3 pl-11">
                  {(task.checks || []).map((check, checkIndex) => {
                    const isSubmitting = submitting[`${task.id}-${check.id}`];
                    const isCompleted = check.response !== null && check.response !== undefined;
                    const ResponseIcon = responseTypeIcons[check.response_type] || ClipboardCheck;
                    
                    return (
                      <div 
                        key={check.id}
                        className={cn(
                          'p-3 rounded-lg border transition-all',
                          isCompleted 
                            ? 'bg-emerald-50 border-emerald-200' 
                            : 'bg-white border-slate-200'
                        )}
                        data-testid={`check-${check.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className={cn(
                              'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                              isCompleted ? 'bg-emerald-100' : 'bg-slate-100'
                            )}>
                              {isCompleted ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <span className="text-xs text-slate-500">{checkIndex + 1}</span>
                              )}
                            </div>
                            <div>
                              <p className={cn(
                                'font-medium',
                                isCompleted ? 'text-emerald-900' : 'text-slate-900'
                              )}>
                                {check.name}
                                {check.is_mandatory && (
                                  <span className="text-red-500 ml-1">*</span>
                                )}
                              </p>
                              {check.description && (
                                <p className="text-sm text-slate-500 mt-0.5">{check.description}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="text-xs">
                                  <ResponseIcon className="w-3 h-3 mr-1" />
                                  {responseTypeLabels[check.response_type]}
                                </Badge>
                                {check.photo_required && (
                                  <Badge variant="outline" className="text-xs">
                                    <Camera className="w-3 h-3 mr-1" />
                                    Photo Required
                                  </Badge>
                                )}
                                {check.response_type === 'numeric' && check.numeric_unit && (
                                  <Badge variant="outline" className="text-xs">
                                    {check.numeric_min !== null && `Min: ${check.numeric_min}`}
                                    {check.numeric_max !== null && ` Max: ${check.numeric_max}`}
                                    {` ${check.numeric_unit}`}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Response Input */}
                        {!isCompleted && (
                          <div className="mt-3 pl-9">
                            <CheckResponseInput
                              check={check}
                              taskId={task.id}
                              isSubmitting={isSubmitting}
                              onSubmit={submitCheck}
                              onPhotoCapture={() => handlePhotoCapture(task.id, check)}
                            />
                          </div>
                        )}
                        
                        {/* Completed Response Display */}
                        {isCompleted && (
                          <div className="mt-2 pl-9">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-slate-500">Response:</span>
                              <Badge className={cn(
                                'capitalize',
                                check.response === 'pass' && 'bg-emerald-100 text-emerald-700',
                                check.response === 'fail' && 'bg-red-100 text-red-700'
                              )}>
                                {check.numeric_value !== null && check.numeric_value !== undefined
                                  ? `${check.numeric_value} ${check.numeric_unit || ''}`
                                  : check.response}
                              </Badge>
                              {check.photo_url && (
                                <Badge variant="outline" className="text-xs">
                                  <ImageIcon className="w-3 h-3 mr-1" />
                                  Photo attached
                                </Badge>
                              )}
                            </div>
                            {check.notes && (
                              <p className="text-sm text-slate-500 mt-1">Note: {check.notes}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Complete Button */}
      <Card className="border-sky-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-slate-900">Ready to Complete?</h4>
              <p className="text-sm text-slate-500">
                {canComplete 
                  ? 'All mandatory checks are complete. You can now finish this visit.'
                  : `Complete ${mandatoryChecks.length - completedMandatory.length} more mandatory check(s) first.`
                }
              </p>
            </div>
            <Button 
              onClick={() => setShowCompleteDialog(true)} 
              disabled={!canComplete}
              data-testid="complete-visit-btn"
            >
              <Flag className="w-4 h-4 mr-2" />
              Complete Visit
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Photo Capture Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Capture Photo</DialogTitle>
            <DialogDescription>
              {currentCheck?.name} - Take or upload a photo for this check
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {photoPreview ? (
              <div className="relative">
                <img 
                  src={photoPreview} 
                  alt="Preview" 
                  className="w-full h-48 object-cover rounded-lg"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-8 w-8"
                  onClick={() => setPhotoPreview(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-sky-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                <p className="text-slate-600 font-medium">Click to capture or upload</p>
                <p className="text-sm text-slate-400 mt-1">JPEG, PNG up to 10MB</p>
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhotoDialog(false)}>
              Cancel
            </Button>
            <Button onClick={submitPhotoCheck} disabled={!photoPreview}>
              <Upload className="w-4 h-4 mr-2" />
              Submit Photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Visit Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Complete Site Visit</DialogTitle>
            <DialogDescription>
              Add a summary and any findings before completing this visit.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="summary">Visit Summary</Label>
              <Textarea
                id="summary"
                value={completionData.report_summary}
                onChange={(e) => setCompletionData(prev => ({ ...prev, report_summary: e.target.value }))}
                placeholder="Summarize the visit and key observations..."
                rows={3}
                data-testid="visit-summary"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={completionData.notes}
                onChange={(e) => setCompletionData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Findings</Label>
              <div className="flex gap-2">
                <Input
                  value={newFinding}
                  onChange={(e) => setNewFinding(e.target.value)}
                  placeholder="Add a finding..."
                  onKeyDown={(e) => e.key === 'Enter' && addFinding()}
                />
                <Button variant="outline" onClick={addFinding}>Add</Button>
              </div>
              {completionData.findings.length > 0 && (
                <div className="space-y-1 mt-2">
                  {completionData.findings.map((finding, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-amber-50 p-2 rounded">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="flex-1">{finding.description}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setCompletionData(prev => ({
                          ...prev,
                          findings: prev.findings.filter((_, idx) => idx !== i)
                        }))}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Recommendations</Label>
              <div className="flex gap-2">
                <Input
                  value={newRecommendation}
                  onChange={(e) => setNewRecommendation(e.target.value)}
                  placeholder="Add a recommendation..."
                  onKeyDown={(e) => e.key === 'Enter' && addRecommendation()}
                />
                <Button variant="outline" onClick={addRecommendation}>Add</Button>
              </div>
              {completionData.recommendations.length > 0 && (
                <div className="space-y-1 mt-2">
                  {completionData.recommendations.map((rec, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-sky-50 p-2 rounded">
                      <FileCheck className="w-4 h-4 text-sky-500" />
                      <span className="flex-1">{rec}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setCompletionData(prev => ({
                          ...prev,
                          recommendations: prev.recommendations.filter((_, idx) => idx !== i)
                        }))}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={completeExecution} data-testid="confirm-complete-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              Complete Visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Check Response Input Component
function CheckResponseInput({ check, taskId, isSubmitting, onSubmit, onPhotoCapture }) {
  const [numericValue, setNumericValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);

  const handlePassFail = (result) => {
    onSubmit(taskId, check, result, null, null, notes);
  };

  const handleNumericSubmit = () => {
    const value = parseFloat(numericValue);
    if (isNaN(value)) return;
    
    // Validate against min/max
    if (check.numeric_min !== null && value < check.numeric_min) {
      alert(`Value must be at least ${check.numeric_min}`);
      return;
    }
    if (check.numeric_max !== null && value > check.numeric_max) {
      alert(`Value must be at most ${check.numeric_max}`);
      return;
    }
    
    onSubmit(taskId, check, 'numeric', value, null, notes);
  };

  const handleTextSubmit = () => {
    if (!textValue.trim()) return;
    onSubmit(taskId, check, textValue, null, null, notes);
  };

  return (
    <div className="space-y-2">
      {/* Pass/Fail Response */}
      {check.response_type === 'pass_fail' && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePassFail('pass')}
            disabled={isSubmitting}
            className="flex-1 bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
            data-testid={`pass-btn-${check.id}`}
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
            Pass
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePassFail('fail')}
            disabled={isSubmitting}
            className="flex-1 bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
            data-testid={`fail-btn-${check.id}`}
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
            Fail
          </Button>
        </div>
      )}

      {/* Numeric Response */}
      {check.response_type === 'numeric' && (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={numericValue}
            onChange={(e) => setNumericValue(e.target.value)}
            placeholder={`Enter value ${check.numeric_unit ? `(${check.numeric_unit})` : ''}`}
            className="flex-1"
            min={check.numeric_min}
            max={check.numeric_max}
            step="0.01"
            data-testid={`numeric-input-${check.id}`}
          />
          <Button
            size="sm"
            onClick={handleNumericSubmit}
            disabled={isSubmitting || !numericValue}
            data-testid={`submit-numeric-${check.id}`}
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      )}

      {/* Text Response */}
      {check.response_type === 'text' && (
        <div className="space-y-2">
          <Textarea
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            placeholder="Enter your response..."
            rows={2}
            data-testid={`text-input-${check.id}`}
          />
          <Button
            size="sm"
            onClick={handleTextSubmit}
            disabled={isSubmitting || !textValue.trim()}
            data-testid={`submit-text-${check.id}`}
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Submit
          </Button>
        </div>
      )}

      {/* Photo Response */}
      {check.response_type === 'photo' && (
        <Button
          variant="outline"
          size="sm"
          onClick={onPhotoCapture}
          disabled={isSubmitting}
          className="w-full"
          data-testid={`photo-btn-${check.id}`}
        >
          <Camera className="w-4 h-4 mr-2" />
          {check.photo_required ? 'Take Required Photo' : 'Add Photo'}
        </Button>
      )}

      {/* Photo Required Badge for other types */}
      {check.photo_required && check.response_type !== 'photo' && (
        <Button
          variant="outline"
          size="sm"
          onClick={onPhotoCapture}
          className="text-xs"
        >
          <Camera className="w-3 h-3 mr-1" />
          Add Photo
        </Button>
      )}

      {/* Notes Toggle */}
      <div>
        {!showNotes ? (
          <button
            className="text-xs text-slate-500 hover:text-slate-700"
            onClick={() => setShowNotes(true)}
          >
            + Add notes
          </button>
        ) : (
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes (optional)"
            className="text-sm"
          />
        )}
      </div>
    </div>
  );
}
